/**
 * The relay's answers, read at the boundary (part 36a, principle C4; Definition of Done, security: nothing unvalidated
 * crosses into the app's types). Each reader takes the parsed JSON of one answer and gives its one shape, or null, which
 * the client treats as a failure, never a success. A reader checks every field it reads, exactly; fields it doesn't read
 * are let be, so a relay that adds one (a later part) doesn't break an app already installed.
 */
import { readKdfRecord } from '@cockatrice/crypto';
import type { KdfRecord } from '@cockatrice/crypto';
import { bytesOfLength, fromBase64Url } from './bytes.ts';

/** An account's ID: a UUID in its canonical lowercase form (the relay's `ids.ts`). */
export type AccountId = string & { readonly __brand: 'AccountId' };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;

/** Whether `value` is an account ID. */
export function isAccountId(value: unknown): value is AccountId {
  return typeof value === 'string' && UUID.test(value);
}

/** A challenge's, a session token's, a ticket's and a link token's bytes (the relay's `CHALLENGE_BYTES` and the rest). */
export const CHALLENGE_BYTES = 32;
export const TOKEN_BYTES = 32;

/** A session as the relay issues it: the bearer token (base64url) and its end. */
export interface IssuedSession {
  readonly token: string;
  readonly expiresAt: number;
}

/** A trial copy as sent (table TR): the signed bytes and the signature, each base64url. */
export interface SignedCopy {
  readonly copy: string;
  readonly signature: string;
}

/** The terms' status (table AR): the current versions, the newest accepted of each, and whether to ask again. */
export interface TermsStatus {
  readonly current: { readonly terms: number; readonly privacy: number };
  readonly accepted: { readonly terms: number | null; readonly privacy: number | null };
  readonly required: boolean;
}

/** LG4, SU6: a device admitted, with its session, its trial copy and the terms; backup codes left after one was used. */
export interface LoggedIn {
  readonly kind: 'logged-in';
  readonly account: AccountId;
  readonly session: IssuedSession;
  readonly newDevice: boolean;
  readonly trial: SignedCopy;
  readonly terms: TermsStatus;
  readonly backupCodesLeft: number | null;
}

/** A passkey prompt's options as the relay sends them (WebAuthn's `PublicKeyCredentialRequestOptionsJSON`). */
export interface PasskeyRequest {
  readonly challenge: string;
  readonly rpId: string | null;
  readonly timeout: number | null;
  readonly userVerification: 'required' | 'preferred' | 'discouraged' | null;
  readonly allowCredentials: readonly { readonly id: string; readonly transports: readonly string[] }[];
}

/** LG5: a right P on an account with F: the ticket for the second step and what the step may take. */
export interface SecondFactorNeeded {
  readonly kind: 'second-factor';
  readonly ticket: string;
  readonly expiresAt: number;
  readonly totp: boolean;
  readonly backupCodes: number;
  readonly passkey: PasskeyRequest | null;
}

/** What a login's first step answers. */
export type LoginAnswer = LoggedIn | SecondFactorNeeded;

/** A refreshed session (table SE): the session and a new trial copy. */
export interface Refreshed {
  readonly session: IssuedSession;
  readonly trial: SignedCopy;
}

/** The account's standing (part 35a, table ST). */
export type Standing = 'guest' | 'paid' | 'trial' | 'read-only';

/** What the account's second factor is, as `GET /v1/account` sums it up (part 29b): decision 24's warning while it's off. */
export interface FactorSummary {
  readonly totp: boolean;
  readonly passkeys: number;
  readonly backupCodes: number;
}

/** A deletion of the account waiting its 30 days (part 35a, DE7): when it was asked, when it is due, and which device asked. */
export interface PendingDeletion {
  readonly requestedAt: number;
  readonly dueAt: number;
  readonly device: string;
}

/**
 * What `GET /v1/account` says of the account: its copy, standing, purge date and terms (part 36a); from part 36c its
 * current address (CH5), its second factor (FX1) and a deletion pending (DA2).
 */
export interface AccountView {
  readonly account: AccountId;
  readonly trial: SignedCopy;
  readonly standing: Standing;
  readonly purgeAt: number | null;
  /** Part 38a (table GC): for an account that began as a guest, when its own shares and links end unless it buys. */
  readonly sharesEndAt: number | null;
  readonly terms: TermsStatus;
  readonly email: string;
  readonly secondFactor: FactorSummary;
  readonly deletion: PendingDeletion | null;
}

type Fields = Readonly<Record<string, unknown>>;

/** A JSON object's fields, or null. */
export function fieldsOf(value: unknown): Fields | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return Object.fromEntries(Object.entries(value));
}

/** A time in milliseconds since the epoch: a whole number of zero or more. */
function timeOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/** A count: a whole number of zero or more. */
function countOf(value: unknown): number | null {
  return timeOf(value);
}

/** A legal document's version: a whole number of one or more. */
function versionOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1 ? value : null;
}

/** A version, or null for none accepted; undefined when it is neither. */
function acceptedOf(value: unknown): number | null | undefined {
  return value === null ? null : (versionOf(value) ?? undefined);
}

function sessionOf(value: unknown): IssuedSession | null {
  const fields = fieldsOf(value);
  const token = fields?.['token'];
  const expiresAt = timeOf(fields?.['expiresAt']);
  return typeof token === 'string' && bytesOfLength(token, TOKEN_BYTES) !== null && expiresAt !== null ? { token, expiresAt } : null;
}

/** A trial copy whose two fields are base64url (what they hold is `trial.ts`'s to check). */
export function signedCopyOf(value: unknown): SignedCopy | null {
  const fields = fieldsOf(value);
  const copy = fields?.['copy'];
  const signature = fields?.['signature'];
  if (typeof copy !== 'string' || typeof signature !== 'string') return null;
  return fromBase64Url(copy) !== null && fromBase64Url(signature) !== null ? { copy, signature } : null;
}

/** The terms' status (AR4). */
export function termsOf(value: unknown): TermsStatus | null {
  const fields = fieldsOf(value);
  const current = fieldsOf(fields?.['current']);
  const accepted = fieldsOf(fields?.['accepted']);
  const terms = versionOf(current?.['terms']);
  const privacy = versionOf(current?.['privacy']);
  const acceptedTerms = acceptedOf(accepted?.['terms']);
  const acceptedPrivacy = acceptedOf(accepted?.['privacy']);
  const required = fields?.['required'];
  if (terms === null || privacy === null || acceptedTerms === undefined || acceptedPrivacy === undefined || typeof required !== 'boolean') return null;
  return { current: { terms, privacy }, accepted: { terms: acceptedTerms, privacy: acceptedPrivacy }, required };
}

/** `{ challenge }`: 32 bytes. */
export function readChallenge(value: unknown): Uint8Array<ArrayBuffer> | null {
  return bytesOfLength(fieldsOf(value)?.['challenge'], CHALLENGE_BYTES);
}

/** `{ kdf }`: a KDF record whose salt is base64url (25a's reader judges the rest; the bounds are the client's, C7). */
export function readKdf(value: unknown): KdfRecord | null {
  const kdf = fieldsOf(fieldsOf(value)?.['kdf']);
  const salt = kdf?.['salt'];
  const bytes = typeof salt === 'string' ? fromBase64Url(salt) : null;
  return kdf === null || bytes === null ? null : readKdfRecord({ ...kdf, salt: bytes });
}

/** `{ status: 'check-email' }`: a signup taken (SU1; the same for every address). */
export function readCheckEmail(value: unknown): boolean {
  return fieldsOf(value)?.['status'] === 'check-email';
}

/** A device admitted (LG4, SU6), or null. */
export function readLoggedIn(value: unknown): LoggedIn | null {
  const fields = fieldsOf(value);
  const account = fields?.['account'];
  const session = sessionOf(fields?.['session']);
  const fresh = fieldsOf(fields?.['device'])?.['new'];
  const trial = signedCopyOf(fields?.['trial']);
  const terms = termsOf(fields?.['terms']);
  const left = fields?.['backupCodesLeft'];
  const backupCodesLeft = left === undefined ? null : countOf(left);
  if (!isAccountId(account) || session === null || typeof fresh !== 'boolean' || trial === null || terms === null) return null;
  if (left !== undefined && backupCodesLeft === null) return null;
  return { kind: 'logged-in', account, session, newDevice: fresh, trial, terms, backupCodesLeft };
}

const VERIFICATIONS = ['required', 'preferred', 'discouraged'] as const;

/** One allowed credential: its ID (base64url) and its transports. */
function allowedOf(value: unknown): { readonly id: string; readonly transports: readonly string[] } | null {
  const fields = fieldsOf(value);
  const id = fields?.['id'];
  const transports = fields?.['transports'] ?? [];
  if (typeof id !== 'string' || fromBase64Url(id) === null || (fields?.['type'] ?? 'public-key') !== 'public-key') return null;
  if (!Array.isArray(transports) || !transports.every((t): t is string => typeof t === 'string')) return null;
  return { id, transports };
}

/** A passkey prompt's options (LG5), or null. */
export function passkeyRequestOf(value: unknown): PasskeyRequest | null {
  const fields = fieldsOf(value);
  const challenge = fields?.['challenge'];
  const rpId = fields?.['rpId'] ?? null;
  const timeout = fields?.['timeout'] ?? null;
  const verification = fields?.['userVerification'] ?? null;
  const allowed = fields?.['allowCredentials'] ?? [];
  if (typeof challenge !== 'string' || fromBase64Url(challenge) === null) return null;
  if ((rpId !== null && typeof rpId !== 'string') || (timeout !== null && timeOf(timeout) === null)) return null;
  const userVerification = verification === null ? null : (VERIFICATIONS.find((v) => v === verification) ?? undefined);
  if (userVerification === undefined || !Array.isArray(allowed)) return null;
  const allowCredentials = allowed.map(allowedOf);
  if (allowCredentials.some((c) => c === null)) return null;
  return { challenge, rpId, timeout: timeout === null ? null : Number(timeout), userVerification, allowCredentials: allowCredentials.filter((c) => c !== null) };
}

/** A login's first answer (LG4, LG5), or null. */
export function readLoginAnswer(value: unknown): LoginAnswer | null {
  const fields = fieldsOf(value);
  if (fields?.['status'] !== 'second-factor') return readLoggedIn(value);
  const ticket = fields['ticket'];
  const expiresAt = timeOf(fields['expiresAt']);
  const factors = fieldsOf(fields['factors']);
  const totp = factors?.['totp'];
  const backupCodes = countOf(factors?.['backupCodes']);
  const given = factors?.['passkey'];
  const passkey = given === null ? null : passkeyRequestOf(given);
  if (typeof ticket !== 'string' || bytesOfLength(ticket, TOKEN_BYTES) === null || expiresAt === null || typeof totp !== 'boolean' || backupCodes === null) return null;
  if (given !== null && passkey === null) return null;
  return { kind: 'second-factor', ticket, expiresAt, totp, backupCodes, passkey };
}

/** A refresh's answer (SE2), or null. */
export function readRefreshed(value: unknown): Refreshed | null {
  const fields = fieldsOf(value);
  const session = sessionOf(fields?.['session']);
  const trial = signedCopyOf(fields?.['trial']);
  return session === null || trial === null ? null : { session, trial };
}

const STANDINGS: readonly Standing[] = ['guest', 'paid', 'trial', 'read-only'];

/** The second factor's summary (FX1), or null. */
function factorSummaryOf(value: unknown): FactorSummary | null {
  const fields = fieldsOf(value);
  const totp = fields?.['totp'];
  const passkeys = countOf(fields?.['passkeys']);
  const backupCodes = countOf(fields?.['backupCodes']);
  return typeof totp === 'boolean' && passkeys !== null && backupCodes !== null ? { totp, passkeys, backupCodes } : null;
}

/** A pending deletion (DA2), null for none; undefined when it is neither. */
function deletionOf(value: unknown): PendingDeletion | null | undefined {
  if (value === null) return null;
  const fields = fieldsOf(value);
  const requestedAt = timeOf(fields?.['requestedAt']);
  const dueAt = timeOf(fields?.['dueAt']);
  const device = fields?.['device'];
  if (requestedAt === null || dueAt === null || typeof device !== 'string' || bytesOfLength(device, DEVICE_ID_BYTES) === null) return undefined;
  return { requestedAt, dueAt, device };
}

/** `GET /v1/account`'s fields the app reads, or null. */
export function readAccountView(value: unknown): AccountView | null {
  const fields = fieldsOf(value);
  const account = fields?.['account'];
  const trial = signedCopyOf(fields?.['trial']);
  const standing = STANDINGS.find((s) => s === fields?.['standing']);
  const given = fields?.['purgeAt'];
  const purgeAt = given === null ? null : timeOf(given);
  // Part 38a: a relay from before it says nothing of it.
  const sharesGiven = fields?.['sharesEndAt'] ?? null;
  const sharesEndAt = sharesGiven === null ? null : timeOf(sharesGiven);
  const terms = termsOf(fields?.['terms']);
  const email = fields?.['email'];
  const secondFactor = factorSummaryOf(fields?.['secondFactor']);
  const deletion = deletionOf(fields?.['deletion']);
  if (!isAccountId(account) || trial === null || standing === undefined || (given !== null && purgeAt === null) || terms === null) return null;
  if (typeof email !== 'string' || secondFactor === null || deletion === undefined || (sharesGiven !== null && sharesEndAt === null)) return null;
  return { account, trial, standing, purgeAt, sharesEndAt, terms, email, secondFactor, deletion };
}

/** A device's ID: the SHA-256 of its key (the relay's `DEVICE_ID_BYTES`, CG18). */
export const DEVICE_ID_BYTES = 32;

/** A grant's bytes (the relay's `GRANT_BYTES`, GR1). */
export const GRANT_BYTES = 32;

/** PF1, PF2: a proof's grant, with the backup codes left when one was used. */
export interface Granted {
  readonly kind: 'granted';
  readonly grant: string;
  readonly backupCodesLeft: number | null;
}

/** What a proof's first step answers: a grant, or the F step to take (PF2). */
export type ProofAnswer = Granted | SecondFactorNeeded;

/** A grant's answer (GR1, GR7, GR9), or null. */
export function readGranted(value: unknown): Granted | null {
  const fields = fieldsOf(value);
  const grant = fields?.['grant'];
  const left = fields?.['backupCodesLeft'];
  const backupCodesLeft = left === undefined ? null : countOf(left);
  if (typeof grant !== 'string' || bytesOfLength(grant, GRANT_BYTES) === null || timeOf(fields?.['expiresAt']) === null || typeof fields?.['factor'] !== 'boolean') return null;
  return left !== undefined && backupCodesLeft === null ? null : { kind: 'granted', grant, backupCodesLeft };
}

/** A proof's first answer (PF1, PF2), or null. */
export function readProofAnswer(value: unknown): ProofAnswer | null {
  if (fieldsOf(value)?.['status'] !== 'second-factor') return readGranted(value);
  const answer = readLoginAnswer(value);
  return answer?.kind === 'second-factor' ? answer : null;
}

/** A change's ID: a whole number of one or more within the column's range (the relay's `MAX_CHANGE_ID`). */
export const MAX_CHANGE_ID = 2 ** 31 - 1;

function changeIdOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1 && value <= MAX_CHANGE_ID ? value : null;
}

/** How far a change got (CH5; the relay's `Advanced`). */
export type Advanced =
  | { readonly kind: 'applied' }
  | { readonly kind: 'void' }
  | { readonly kind: 'check-email'; readonly change: number }
  | { readonly kind: 'pending'; readonly change: number; readonly dueAt: number };

/** CH5: `{ status }`, with the change for `check-email` and the change and its time for `pending`; null otherwise. */
export function readAdvanced(value: unknown): Advanced | null {
  const fields = fieldsOf(value);
  const status = fields?.['status'];
  if (status === 'applied' || status === 'void') return { kind: status };
  const change = changeIdOf(fields?.['change']);
  if (status === 'check-email' && change !== null) return { kind: 'check-email', change };
  const dueAt = timeOf(fields?.['dueAt']);
  return status === 'pending' && change !== null && dueAt !== null ? { kind: 'pending', change, dueAt } : null;
}

/** BC1: a backup code as the relay shows it: three groups of four of Crockford's base32, joined by hyphens. */
const SHOWN_CODE = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{4}-[0-9ABCDEFGHJKMNPQRSTVWXYZ]{4}-[0-9ABCDEFGHJKMNPQRSTVWXYZ]{4}$/u;

/** How many codes a set holds (the relay's `BACKUP_CODES`). */
export const BACKUP_CODES = 10;

/** FX4, FX8: a set of backup codes: exactly {@link BACKUP_CODES}, each as shown, none twice; null otherwise. */
export function codesOf(value: unknown): readonly string[] | null {
  if (!Array.isArray(value) || value.length !== BACKUP_CODES) return null;
  const codes = value.filter((code): code is string => typeof code === 'string' && SHOWN_CODE.test(code));
  return codes.length === BACKUP_CODES && new Set(codes).size === BACKUP_CODES ? codes : null;
}

/** FX4: an enrolment's confirmation: its backup codes, or null. */
export function readEnrolled(value: unknown): readonly string[] | null {
  return codesOf(fieldsOf(value)?.['backupCodes']);
}

/** FX8: new backup codes and how far their change got, or null. */
export function readNewCodes(value: unknown): { readonly outcome: Advanced; readonly codes: readonly string[] } | null {
  const outcome = readAdvanced(value);
  const codes = codesOf(fieldsOf(value)?.['backupCodes']);
  return outcome === null || codes === null ? null : { outcome, codes };
}

/** A passkey's creation prompt as the relay sends it (WebAuthn's `PublicKeyCredentialCreationOptionsJSON`, as far as used). */
export interface PasskeyCreation {
  readonly challenge: string;
  readonly rp: { readonly name: string; readonly id: string | null };
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly algorithms: readonly number[];
  readonly timeout: number | null;
  readonly excluded: readonly string[];
  readonly residentKey: 'required' | 'preferred' | 'discouraged' | null;
  readonly userVerification: 'required' | 'preferred' | 'discouraged' | null;
}

const PREFERENCES = ['required', 'preferred', 'discouraged'] as const;

/** One of {@link PREFERENCES}, null for none given; undefined for anything else. */
function preferenceOf(value: unknown): (typeof PREFERENCES)[number] | null | undefined {
  return value === undefined ? null : PREFERENCES.find((p) => p === value);
}

/** A list of credential IDs to exclude (base64url), or null. */
function excludedOf(value: unknown): readonly string[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;
  const ids = value.map((entry) => fieldsOf(entry)?.['id']).filter((id): id is string => typeof id === 'string' && fromBase64Url(id) !== null);
  return ids.length === value.length ? ids : null;
}

/** FX3: a passkey's creation prompt (EN8, FC5), or null. */
export function passkeyCreationOf(value: unknown): PasskeyCreation | null {
  const fields = fieldsOf(value);
  const challenge = fields?.['challenge'];
  const rp = fieldsOf(fields?.['rp']);
  const user = fieldsOf(fields?.['user']);
  const params = fields?.['pubKeyCredParams'];
  const timeout = fields?.['timeout'] ?? null;
  const selection = fieldsOf(fields?.['authenticatorSelection'] ?? {});
  const excluded = excludedOf(fields?.['excludeCredentials']);
  if (typeof challenge !== 'string' || fromBase64Url(challenge) === null || rp === null || user === null || !Array.isArray(params)) return null;
  const rpName = rp['name'];
  const rpId = rp['id'] ?? null;
  const userId = user['id'];
  const userName = user['name'];
  const displayName = user['displayName'];
  if (typeof rpName !== 'string' || (rpId !== null && typeof rpId !== 'string') || typeof userId !== 'string' || fromBase64Url(userId) === null) return null;
  if (typeof userName !== 'string' || typeof displayName !== 'string' || (timeout !== null && timeOf(timeout) === null) || excluded === null) return null;
  const algorithms = params.map((param) => fieldsOf(param)).filter((param) => param?.['type'] === 'public-key' && Number.isSafeInteger(param['alg'])).map((param) => Number(param?.['alg']));
  const residentKey = preferenceOf(selection?.['residentKey']);
  const userVerification = preferenceOf(selection?.['userVerification']);
  if (algorithms.length === 0 || algorithms.length !== params.length || residentKey === undefined || userVerification === undefined) return null;
  return {
    challenge,
    rp: { name: rpName, id: rpId },
    user: { id: userId, name: userName, displayName },
    algorithms,
    timeout: timeout === null ? null : Number(timeout),
    excluded,
    residentKey,
    userVerification,
  };
}

/** A second factor's setup (FX3, FX6): an app's seed (base32) and its `otpauth` URI, or a passkey's creation prompt. */
export type FactorSetup = { readonly kind: 'totp'; readonly secret: string; readonly uri: string } | { readonly kind: 'passkey'; readonly options: PasskeyCreation };

/** A TOTP seed as the relay sends it: RFC 4648 base32, no padding (20 bytes: 32 characters). */
const SECRET = /^[A-Z2-7]{16,64}$/u;

/**
 * Fix-next 85 (the security audit of 2026-11-02, run 2): the one `secret` an `otpauth://totp/` URI names, or null (none
 * or two), so the code shown and the one the QR code carries are the same seed. The caller checked the scheme and host
 * first; a URI of them always parses (its host is `totp`, the rest a path and query).
 */
function uriSecretOf(uri: string): string | null {
  const secrets = new URL(uri).searchParams.getAll('secret');
  return secrets.length === 1 ? secrets.join('') : null;
}

/** FX3: the setup in `fields`, or null. */
function setupOf(fields: Readonly<Record<string, unknown>> | null): FactorSetup | null {
  const kind = fields?.['kind'];
  if (kind === 'totp') {
    const secret = fields?.['secret'];
    const uri = fields?.['uri'];
    return typeof secret === 'string' && SECRET.test(secret) && typeof uri === 'string' && uri.startsWith('otpauth://totp/') && uri.length <= 2048 && uriSecretOf(uri) === secret
      ? { kind, secret, uri }
      : null;
  }
  const options = kind === 'passkey' ? passkeyCreationOf(fields?.['options']) : null;
  return options === null ? null : { kind: 'passkey', options };
}

/** EN8: an enrolment's setup, or null. */
export function readSetup(value: unknown): FactorSetup | null {
  return setupOf(fieldsOf(value));
}

/** FX6: an add's start: `set-up`, the change's ID and its setup, or null. */
export function readAddStarted(value: unknown): { readonly change: number; readonly setup: FactorSetup } | null {
  const fields = fieldsOf(value);
  const change = changeIdOf(fields?.['change']);
  const setup = setupOf(fields);
  return fields?.['status'] === 'set-up' && change !== null && setup !== null ? { change, setup } : null;
}

/** DV1: a device of the account as the list shows it. */
export interface DeviceEntry {
  readonly id: string;
  readonly founding: boolean;
  readonly ageFrom: number;
  readonly new: boolean;
  readonly lastUsedAt: number;
  readonly revokedAt: number | null;
  readonly loggedOutAt: number | null;
  readonly current: boolean;
}

/** A time, or null for none; undefined when it is neither. */
function optionalTimeOf(value: unknown): number | null | undefined {
  return value === null ? null : (timeOf(value) ?? undefined);
}

function deviceEntryOf(value: unknown): DeviceEntry | null {
  const fields = fieldsOf(value);
  const id = fields?.['id'];
  const ageFrom = timeOf(fields?.['ageFrom']);
  const lastUsedAt = timeOf(fields?.['lastUsedAt']);
  const revokedAt = optionalTimeOf(fields?.['revokedAt']);
  const loggedOutAt = optionalTimeOf(fields?.['loggedOutAt']);
  const founding = fields?.['founding'];
  const fresh = fields?.['new'];
  const current = fields?.['current'];
  if (typeof id !== 'string' || bytesOfLength(id, DEVICE_ID_BYTES) === null || ageFrom === null || lastUsedAt === null || revokedAt === undefined || loggedOutAt === undefined) return null;
  if (typeof founding !== 'boolean' || typeof fresh !== 'boolean' || typeof current !== 'boolean') return null;
  return { id, founding, ageFrom, new: fresh, lastUsedAt, revokedAt, loggedOutAt, current };
}

/** Every entry of `list` read by `read`, or null when the list or any entry doesn't read. */
function listOf<T>(value: unknown, read: (entry: unknown) => T | null): readonly T[] | null {
  if (!Array.isArray(value)) return null;
  const entries: T[] = [];
  for (const entry of value) {
    const one = read(entry);
    if (one === null) return null;
    entries.push(one);
  }
  return entries;
}

/** DV1: `{ devices }`, or null. */
export function readDevices(value: unknown): readonly DeviceEntry[] | null {
  return listOf(fieldsOf(value)?.['devices'], deviceEntryOf);
}

/** What a change does (the relay's `ChangeKind`). */
export type ChangeKind = 'password' | 'email' | 'revoke' | 'factor-remove' | 'factor-add' | 'backup-codes' | 'reset';

const CHANGE_KINDS: readonly ChangeKind[] = ['password', 'email', 'revoke', 'factor-remove', 'factor-add', 'backup-codes', 'reset'];

/** PD1: an open change of the account: waiting for its link (`open`, until `expiresAt`) or its time (`pending`, `dueAt`). */
export type ChangeEntry = {
  readonly id: number;
  readonly kind: ChangeKind;
  readonly createdAt: number;
  /** The device that started it, by its ID. */
  readonly device: string;
} & ({ readonly state: 'open'; readonly expiresAt: number } | { readonly state: 'pending'; readonly dueAt: number });

function changeEntryOf(value: unknown): ChangeEntry | null {
  const fields = fieldsOf(value);
  const id = changeIdOf(fields?.['id']);
  const kind = CHANGE_KINDS.find((k) => k === fields?.['kind']);
  const state = fields?.['state'];
  const createdAt = timeOf(fields?.['createdAt']);
  const device = fields?.['device'];
  if (id === null || kind === undefined || createdAt === null || typeof device !== 'string' || bytesOfLength(device, DEVICE_ID_BYTES) === null) return null;
  // CG15: an open change lives until its link's hour ends; a pending one until its time (the relay gives each its own).
  const expiresAt = timeOf(fields?.['expiresAt']);
  const dueAt = timeOf(fields?.['dueAt']);
  if (state === 'open') return expiresAt === null ? null : { id, kind, createdAt, device, state, expiresAt };
  return state === 'pending' && dueAt !== null ? { id, kind, createdAt, device, state, dueAt } : null;
}

/** PD1: `{ changes }`, or null. */
export function readChanges(value: unknown): readonly ChangeEntry[] | null {
  return listOf(fieldsOf(value)?.['changes'], changeEntryOf);
}

/** FX5: the account's second factors (FC16): the app's (its time), each passkey (its ID and time) and the codes left. */
export interface Factors {
  readonly totp: { readonly createdAt: number } | null;
  readonly passkeys: readonly { readonly id: string; readonly createdAt: number }[];
  readonly backupCodes: number;
}

function passkeyEntryOf(value: unknown): { readonly id: string; readonly createdAt: number } | null {
  const fields = fieldsOf(value);
  const id = fields?.['id'];
  const createdAt = timeOf(fields?.['createdAt']);
  return typeof id === 'string' && fromBase64Url(id) !== null && id !== '' && createdAt !== null ? { id, createdAt } : null;
}

/** FX5: `GET /v1/account/second-factor`'s answer, or null. */
export function readFactors(value: unknown): Factors | null {
  const fields = fieldsOf(value);
  const given = fields?.['totp'];
  const totpTime = given === null ? null : timeOf(fieldsOf(given)?.['createdAt']);
  const passkeys = listOf(fields?.['passkeys'], passkeyEntryOf);
  const backupCodes = countOf(fields?.['backupCodes']);
  if ((given !== null && totpTime === null) || passkeys === null || backupCodes === null) return null;
  return { totp: totpTime === null ? null : { createdAt: totpTime }, passkeys, backupCodes };
}

/** Every kind of notice the app words (the relay's `NoticeKind`); any other reads as `other` (a later relay's). */
export const NOTICE_KINDS = [
  'new-device',
  'second-factor-on',
  'second-factor-failed',
  'backup-code-used',
  'change-pending',
  'password-changed',
  'email-changed',
  'device-revoked',
  'change-cancelled',
  'change-void',
  'reset-pending',
  'password-reset',
  'second-factor-added',
  'second-factor-removed',
  'second-factor-off',
  'backup-codes-replaced',
  'deletion-pending',
  'deletion-cancelled',
  'purge-warning',
  // Part 38a's deep-fix r2: an account that began as a guest: its own shares and links go at the date.
  'shares-warning',
] as const;

/** One of {@link NOTICE_KINDS}, or `other`. */
export type NoticeKind = (typeof NOTICE_KINDS)[number] | 'other';

/** NO1: a notice of the account: its ID, kind, time and the device it names (null: none). */
export interface Notice {
  readonly id: number;
  readonly kind: NoticeKind;
  readonly at: number;
  readonly device: string | null;
}

function noticeOf(value: unknown): Notice | null {
  const fields = fieldsOf(value);
  const id = changeIdOf(fields?.['id']);
  const kind = fields?.['kind'];
  const at = timeOf(fields?.['at']);
  const device = fields?.['device'];
  if (id === null || typeof kind !== 'string' || at === null || (device !== null && (typeof device !== 'string' || bytesOfLength(device, DEVICE_ID_BYTES) === null))) return null;
  return { id, kind: NOTICE_KINDS.find((k) => k === kind) ?? 'other', at, device };
}

/** NO1: `{ notices }`, or null. */
export function readNotices(value: unknown): readonly Notice[] | null {
  return listOf(fieldsOf(value)?.['notices'], noticeOf);
}

/** RQ2: a reset's claim: pending, with its account, change and time; or null. */
export function readResetClaimed(value: unknown): { readonly account: AccountId; readonly change: number; readonly dueAt: number } | null {
  const fields = fieldsOf(value);
  const account = fields?.['account'];
  const change = changeIdOf(fields?.['change']);
  const dueAt = timeOf(fields?.['dueAt']);
  return fields?.['status'] === 'pending' && isAccountId(account) && change !== null && dueAt !== null ? { account, change, dueAt } : null;
}

/** DA1: a deletion asked: pending, with its time; or null. */
export function readDeletionPending(value: unknown): { readonly dueAt: number } | null {
  const fields = fieldsOf(value);
  const dueAt = timeOf(fields?.['dueAt']);
  return fields?.['status'] === 'pending' && dueAt !== null ? { dueAt } : null;
}

/** PD2, DA2, DA3: `{ status: 'cancelled' }`. */
export function readCancelled(value: unknown): true | null {
  return fieldsOf(value)?.['status'] === 'cancelled' ? true : null;
}

/** TA2: the terms' status after an acceptance, or null. */
export function readTermsAccepted(value: unknown): TermsStatus | null {
  return termsOf(fieldsOf(value)?.['terms']);
}

/** XP1: the export: any JSON object of the export's format 1 and up (the file is the relay's own answer, kept whole). */
export function readExport(value: unknown): Readonly<Record<string, unknown>> | null {
  const fields = fieldsOf(value);
  const format = fields?.['format'];
  return fields !== null && typeof format === 'number' && Number.isSafeInteger(format) && format >= 1 ? fields : null;
}
